/**
 * Utilitários e funções auxiliares para trabalhar com localizações
 */

import type { Location, LocationWithCEP } from './base';
import { ADDRESS_PATTERNS, BRAZIL_BOUNDS, DEFAULT_GEOGRAPHIC_VALIDATION_CONFIG } from './config';
import type { Coordinates, GeographicValidationResult } from './base';
import { getDistance } from 'geolib';

// Resultado da validação de uma Location
export interface LocationValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

// Resultado da análise de um endereço
export interface AddressAnalysis {
  type: 'cep_only' | 'address_with_cep' | 'address_only' | 'invalid';
  extractedCep?: string;
  isBrazilian: boolean;
  confidence: 'high' | 'medium' | 'low';
  suggestions?: string[];
}

// Utilitários para validação de dados
export class LocationUtils {
  /**
   * Detecta se uma string é um CEP válido
   */
  static isCEP(text: string): boolean {
    const cleanText = text.replace(/\s/g, '');
    return ADDRESS_PATTERNS.cep.some(pattern => pattern.test(cleanText));
  }

  /**
   * Extrai CEP de um texto
   */
  static extractCEP(text: string): string | null {
    for (const pattern of ADDRESS_PATTERNS.cep) {
      const match = text.match(pattern);
      if (match) {
        return match[0].replace(/\D/g, ''); // Retorna apenas números
      }
    }
    return null;
  }

  /**
   * Verifica se é um endereço brasileiro
   */
  static isBrazilianAddress(address: string): boolean {
    return ADDRESS_PATTERNS.brazilianIndicators.some(pattern =>
      pattern.test(address)
    ) || ADDRESS_PATTERNS.brazilianStates.test(address);
  }

  /**
   * Limpa e formata um CEP
   */
  static formatCEP(cep: string): string {
    const clean = cep.replace(/\D/g, '');
    if (clean.length === 8) {
      return `${clean.slice(0, 5)}-${clean.slice(5)}`;
    }
    return cep;
  }

  /**
   * Valida se uma Location tem dados mínimos necessários
   */
  static validateLocation(location: Location): LocationValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validações obrigatórias
    if (!location.endereco?.trim()) {
      errors.push('Endereço é obrigatório');
    }

    if (!location.cep?.trim()) {
      warnings.push('CEP não informado - funcionalidade híbrida limitada');
    } else if (!this.isCEP(location.cep)) {
      errors.push('CEP em formato inválido');
    }

    // Validações de qualidade
    if (location.endereco && location.endereco.length < 10) {
      warnings.push('Endereço parece muito curto');
    }

    if (location.endereco && !this.isBrazilianAddress(location.endereco)) {
      warnings.push('Endereço pode não ser brasileiro');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Valida se uma LocationWithCEP tem dados completos
   */
  static validateLocationWithCEP(location: LocationWithCEP): LocationValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validações obrigatórias
    if (!location.endereco?.trim()) {
      errors.push('Endereço é obrigatório');
    }

    if (!location.cep?.trim()) {
      errors.push('CEP é obrigatório');
    } else if (!this.isCEP(location.cep)) {
      errors.push('CEP em formato inválido');
    }

    // Validações de qualidade
    if (location.endereco && location.endereco.length < 10) {
      warnings.push('Endereço parece muito curto');
    }

    if (location.endereco && !this.isBrazilianAddress(location.endereco)) {
      warnings.push('Endereço pode não ser brasileiro');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Converte Location para LocationWithCEP extraindo CEP do endereço se necessário
   */
  static toLocationWithCEP(location: Location): LocationWithCEP | null {
    let cep = location.cep;

    // Se não tem CEP, tenta extrair do endereço
    if (!cep) {
      const extractedCep = this.extractCEP(location.endereco);
      cep = extractedCep || undefined;
    }

    if (!cep) {
      return null; // Não é possível criar LocationWithCEP sem CEP
    }

    return {
      id_location: location.id_location,
      id_nome: location.id_nome,
      id_notificador: location.id_notificador,
      id_endereco: location.id_endereco,
      protocolo: location.protocolo,
      endereco: location.endereco,
      notificado: location.notificado,
      cep: cep
    };
  }

  /**
   * Verifica se uma Location tem CEP disponível (direto ou extraível)
   */
  static hasCEP(location: Location): boolean {
    return !!(location.cep || this.extractCEP(location.endereco));
  }

  /**
   * Analisa um endereço e retorna informações sobre sua estrutura
   */
  static analyzeAddress(address: string): AddressAnalysis {
    const extractedCep = this.extractCEP(address);
    const isBrazilian = this.isBrazilianAddress(address);
    const hasOnlyCep = this.isCEP(address.trim());

    let type: AddressAnalysis['type'];
    let confidence: AddressAnalysis['confidence'];
    const suggestions: string[] = [];

    if (hasOnlyCep) {
      type = 'cep_only';
      confidence = 'high';
    } else if (extractedCep && address.length > 15) {
      type = 'address_with_cep';
      confidence = 'high';
    } else if (address.length > 10 && !extractedCep) {
      type = 'address_only';
      confidence = isBrazilian ? 'medium' : 'low';
      if (!isBrazilian) {
        suggestions.push('Adicione estado brasileiro ou CEP para melhor precisão');
      }
    } else {
      type = 'invalid';
      confidence = 'low';
      suggestions.push('Endereço muito curto ou em formato inválido');
    }

    return {
      type,
      extractedCep: extractedCep || undefined,
      isBrazilian,
      confidence,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  }

  /**
   * Normaliza um endereço para melhor geocodificação
   */
  static normalizeAddress(address: string): string {
    return address
      .trim()
      .replace(/\s+/g, ' ') // Remove espaços duplos
      .replace(/,\s*,/g, ',') // Remove vírgulas duplas
      .replace(/\s*,\s*/g, ', ') // Normaliza espaçamento das vírgulas
      .replace(/\.$/, ''); // Remove ponto final
  }

  /**
   * Constrói um endereço completo a partir de componentes
   */
  static buildFullAddress(components: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  }): string {
    const parts: string[] = [];

    if (components.logradouro) {
      let street = components.logradouro;
      if (components.numero) {
        street += `, ${components.numero}`;
      }
      parts.push(street);
    }

    if (components.complemento) {
      parts.push(components.complemento);
    }

    if (components.bairro) {
      parts.push(components.bairro);
    }

    if (components.cidade) {
      parts.push(components.cidade);
    }

    if (components.uf) {
      parts.push(components.uf);
    }

    if (components.cep) {
      parts.push(`CEP ${this.formatCEP(components.cep)}`);
    }

    return parts.join(', ');
  }
}

// Utilitários para validação geográfica
export class GeographicValidator {
  /**
   * Verifica se coordenadas estão dentro do território brasileiro
   */
  static isWithinBrazil(coordinates: Coordinates): boolean {
    const { latitude, longitude } = coordinates;
    const bounds = BRAZIL_BOUNDS;

    return (
      latitude >= bounds.south &&
      latitude <= bounds.north &&
      longitude >= bounds.west &&
      longitude <= bounds.east
    );
  }

  /**
   * Calcula distância do centro geográfico do Brasil
   */
  static distanceFromBrazilCenter(coordinates: Coordinates): number {
    return getDistance(
      { lat: coordinates.latitude, lng: coordinates.longitude },
      { lat: BRAZIL_BOUNDS.center.latitude, lng: BRAZIL_BOUNDS.center.longitude }
    );
  }

  /**
   * Validação completa de coordenadas brasileiras
   */
  static validateBrazilianCoordinates(
    coordinates: Coordinates,
    config = DEFAULT_GEOGRAPHIC_VALIDATION_CONFIG
  ): GeographicValidationResult {
    if (!config.enableBrazilValidation) {
      return {
        isWithinBrazil: true,
        distanceFromCenter: 0,
        warningLevel: 'none',
        message: 'Validação geográfica desabilitada',
        coordinates
      };
    }

    const isWithinBrazil = this.isWithinBrazil(coordinates);
    const distanceFromCenter = this.distanceFromBrazilCenter(coordinates);

    let warningLevel: GeographicValidationResult['warningLevel'] = 'none';
    let message = '';

    if (!isWithinBrazil) {
      // Verifica se está próximo das fronteiras (tolerância)
      const nearBorderDistance = this.calculateBorderDistance(coordinates);

      if (nearBorderDistance <= config.allowNearBorderTolerance) {
        warningLevel = 'border_close';
        message = `Coordenadas próximas à fronteira (${Math.round(nearBorderDistance / 1000)}km da fronteira)`;
      } else {
        warningLevel = 'outside_brazil';
        message = `Coordenadas fora do território brasileiro (${Math.round(nearBorderDistance / 1000)}km da fronteira)`;
      }

      return {
        isWithinBrazil: nearBorderDistance <= config.allowNearBorderTolerance,
        distanceFromCenter,
        nearestBorderDistance: nearBorderDistance,
        warningLevel,
        message,
        coordinates
      };
    }

    // Dentro do Brasil - verifica distâncias suspeitas
    if (distanceFromCenter > config.warnOnSuspiciousDistance) {
      warningLevel = 'suspicious';
      message = `Coordenadas muito distantes do centro do Brasil (${Math.round(distanceFromCenter / 1000)}km)`;
    } else {
      message = `Coordenadas válidas no território brasileiro (${Math.round(distanceFromCenter / 1000)}km do centro)`;
    }

    return {
      isWithinBrazil: true,
      distanceFromCenter,
      warningLevel,
      message,
      coordinates
    };
  }

  /**
   * Calcula distância aproximada da fronteira mais próxima
   */
  private static calculateBorderDistance(coordinates: Coordinates): number {
    const { latitude, longitude } = coordinates;
    const bounds = BRAZIL_BOUNDS;

    // Calcula distâncias para cada fronteira
    const distances = [];

    // Norte
    if (latitude > bounds.north) {
      distances.push(Math.abs(latitude - bounds.north) * 111000); // ~111km por grau
    }

    // Sul
    if (latitude < bounds.south) {
      distances.push(Math.abs(latitude - bounds.south) * 111000);
    }

    // Leste
    if (longitude > bounds.east) {
      distances.push(Math.abs(longitude - bounds.east) * 111000 * Math.cos(latitude * Math.PI / 180));
    }

    // Oeste
    if (longitude < bounds.west) {
      distances.push(Math.abs(longitude - bounds.west) * 111000 * Math.cos(latitude * Math.PI / 180));
    }

    // Retorna a menor distância (fronteira mais próxima)
    return distances.length > 0 ? Math.min(...distances) : 0;
  }

  /**
   * Sugere correções para coordenadas inválidas
   */
  static suggestCorrection(coordinates: Coordinates): {
    suggestion: string;
    correctedCoordinates?: Coordinates;
  } {
    const validation = this.validateBrazilianCoordinates(coordinates);

    if (validation.isWithinBrazil) {
      return { suggestion: 'Coordenadas válidas' };
    }

    // Análise de possíveis erros comuns
    const { latitude, longitude } = coordinates;

    // Inversão de latitude/longitude
    if (this.isWithinBrazil({ latitude: longitude, longitude: latitude })) {
      return {
        suggestion: 'Possível inversão de latitude e longitude',
        correctedCoordinates: { latitude: longitude, longitude: latitude }
      };
    }

    // Sinal invertido
    if (this.isWithinBrazil({ latitude: -latitude, longitude })) {
      return {
        suggestion: 'Possível sinal invertido na latitude',
        correctedCoordinates: { latitude: -latitude, longitude }
      };
    }

    if (this.isWithinBrazil({ latitude, longitude: -longitude })) {
      return {
        suggestion: 'Possível sinal invertido na longitude',
        correctedCoordinates: { latitude, longitude: -longitude }
      };
    }

    // Ambos os sinais invertidos
    if (this.isWithinBrazil({ latitude: -latitude, longitude: -longitude })) {
      return {
        suggestion: 'Possível sinal invertido em ambas coordenadas',
        correctedCoordinates: { latitude: -latitude, longitude: -longitude }
      };
    }

    return {
      suggestion: `Coordenadas fora do Brasil. Verifique o endereço ou use um endereço brasileiro válido.`
    };
  }
}